import type { MutableRefObject, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { useQuery as ReactQueryUseQuery } from 'react-query';
import { observer } from 'mobx-react';

import type {
  GetAllOpenQuestionAnswersResponse,
  GetSurveyResultResponse,
  GetUserQuestionAnswersRankingResponse,
  TrackingDataCommonAnswerResponse,
} from '@genially/contracts';
import type { InteractiveQuestionsItemData } from '@genially/document-model';
import { InteractiveQuestionType } from '@genially/document-model';
import { calculate } from '@genially/utils';

import type { Language } from '../../utils/language';
import { useFetchAggregatedResults } from './hooks/useFetchAggregatedResults';
import { SidebarOpenReason } from './types/SidebarOpenReason';
import type { SidebarType } from './types/SidebarType';

const DEFAULT_ANIMATION_TIME = 2500;

export type SelectedInteractiveQuestion<T = InteractiveQuestionsItemData> = T & {
  Id: string;
};

type FetchReturnType =
  | GetSurveyResultResponse
  | GetUserQuestionAnswersRankingResponse
  | GetAllOpenQuestionAnswersResponse;

export type SidebarSummaryContextState =
  | {
      isOpen: true;
      reason: SidebarOpenReason;
      toggleOpen: (isOpen?: boolean) => void;
      onSidebarCloseTransitionEnd: () => void;
    }
  | {
      isOpen: false;
      toggleOpen: (isOpen?: boolean) => void;
      onSidebarCloseTransitionEnd: () => void;
    };

export type SidebarSummaryContextData = {
  data: FetchReturnType | undefined;
  shouldFetch: boolean;
  fetch: () => Promise<void>;
  lastUpdated: Date | undefined;
};

export type SidebarSummaryContextAnimation = {
  finished: boolean;
  start: () => void;
  stop: () => void;
};

export type SidebarSummaryContextConfig = {
  isMobile: boolean;
  isAudioMuted: boolean;
  language: Language;
  autoExpandOnOpen?: boolean;
  getTranslation: (key: string) => string;
  toggleOpenAnswerVisibility?: (userAlias: string) => void;

  /**
   * Omit animations before show question content on the sidebar
   *
   */
  omitAnimations?: boolean;

  /**
   * Omit podium animation on scoreable questions
   */
  disablePodiumAnimations?: boolean;

  /**
   * Hide the last updated at the bottom of the sidebar
   */
  hideLastUpdated?: boolean;

  /**
   * If true, the user will be counted as a participant in the ranking
   */
  countMySelfAsParticipant?: boolean;

  /**
   * Time in milliseconds to refresh the data.
   */
  fetchingRefreshTime: number;

  /**
   * Maximum number of calls to fetch the data.
   * If the number of calls is reached, the fetching will stop.
   */
  fetchingMaxCalls: number;

  onAnimationEnd?: (type: SidebarType) => void;
};

export type SidebarSummarySelectedQuestion<T = SelectedInteractiveQuestion> = T & {
  isCorrect: boolean;
  isLast: boolean;
};

export type SidebarSummaryCurrentUser = {
  alias?: string;
  isAuthenticated: boolean;
};

export interface SidebarSummaryContextType<T = SelectedInteractiveQuestion> {
  state: SidebarSummaryContextState;

  currentUser: SidebarSummaryCurrentUser;

  type: SidebarType;

  summary: SidebarSummaryContextData;

  animation: SidebarSummaryContextAnimation;

  selectedQuestion?: SidebarSummarySelectedQuestion<T> | undefined;

  config: SidebarSummaryContextConfig;

  selectedQuestionAnswer?: TrackingDataCommonAnswerResponse & {
    isCorrect?: boolean;
  };
}

const SidebarSummaryContext = createContext({});

export interface SidebarSummaryProviderProps
  extends Omit<SidebarSummaryContextType, 'animation' | 'summary'> {
  children: ReactNode;
  fetchSummary: () => Promise<FetchReturnType>;
  useQuery: typeof ReactQueryUseQuery;
}

const SidebarSummaryProvider = observer(
  ({
    children,
    type,
    currentUser,
    selectedQuestion,
    state,
    fetchSummary,
    config,
    selectedQuestionAnswer,
    useQuery,
  }: SidebarSummaryProviderProps) => {
    const [isAnimationFinished, setIsAnimationFinished] = useState(
      !!config.omitAnimations,
    );

    const timeout: MutableRefObject<NodeJS.Timeout | undefined> = useRef(undefined);

    const shouldFetch = calculate(() => {
      if (!state.isOpen) {
        return false;
      }

      if (state.reason !== SidebarOpenReason.Response) {
        return true;
      }

      return isAnimationFinished;
    });

    const cacheKey = useMemo(() => {
      if (selectedQuestion?.Type === InteractiveQuestionType.Survey) {
        return 'survey';
      }

      if (selectedQuestion?.Type === InteractiveQuestionType.OpenAnswer) {
        return 'openAnswers';
      }

      return 'ranking';
    }, [selectedQuestion]);

    const query = useQuery<FetchReturnType>(cacheKey, fetchSummary, {
      enabled: false,
    });

    const { data, lastUpdated, fetchAggregatedData } = useFetchAggregatedResults({
      shouldFetch,
      query,
      isSidebarOpen: state.isOpen,
      selectedQuestion,

      options: {
        fetchRefreshTime: config.fetchingRefreshTime,
        maxCalls: config.fetchingMaxCalls,
      },
    });

    const startAnimation = () => {
      if (timeout.current) {
        return;
      }

      timeout.current = setTimeout(() => {
        setIsAnimationFinished(true);
      }, DEFAULT_ANIMATION_TIME);
    };

    const stopAnimation = useCallback(() => {
      if (timeout.current) {
        clearTimeout(timeout.current);

        timeout.current = undefined;
      }

      setIsAnimationFinished(!!config.omitAnimations);
    }, [config.omitAnimations]);

    const restartAnimation = useCallback(() => {
      stopAnimation();
      startAnimation();
    }, [stopAnimation]);

    const context = useMemo(
      (): SidebarSummaryContextType => ({
        type,

        state,

        summary: {
          data,
          shouldFetch,
          fetch: fetchAggregatedData,
          lastUpdated,
        },

        config: {
          ...config,
          countMySelfAsParticipant: config.countMySelfAsParticipant ?? true,
        },

        selectedQuestion,

        currentUser,

        animation: {
          finished: isAnimationFinished,
          start: startAnimation,
          stop: stopAnimation,
        },

        selectedQuestionAnswer,
      }),
      [
        stopAnimation,
        config,
        currentUser,
        data,
        fetchAggregatedData,
        isAnimationFinished,
        lastUpdated,
        selectedQuestion,
        shouldFetch,
        state,
        type,
        selectedQuestionAnswer,
      ],
    );

    useEffect(() => {
      if (selectedQuestion) {
        restartAnimation();
      } else {
        stopAnimation();
      }
    }, [restartAnimation, selectedQuestion, stopAnimation]);

    return (
      <SidebarSummaryContext.Provider value={context}>
        {children}
      </SidebarSummaryContext.Provider>
    );
  },
);

const useSidebarSummaryContext = <T = InteractiveQuestionsItemData,>() => {
  const context = useContext(SidebarSummaryContext) as SidebarSummaryContextType<
    SelectedInteractiveQuestion<T>
  >;

  if (Object.keys(context).length === 0) {
    throw new Error('useSidebarSummary must be used within a SidebarSummaryProvider');
  }

  return context;
};

export { SidebarSummaryProvider, useSidebarSummaryContext };
